import type { StateCreator } from 'zustand'

// Cross-module action history. Modules push entries when they perform
// a user-visible mutation (BoQ write, IFCEdit move, etc.). The slice
// only stores metadata + the undo/redo callbacks the module supplies;
// the actual rollback logic lives in the module so it can use its
// own primitives (e.g. BoqSession.undoWrite).
//
// Kept deliberately minimal; onda 5 will revisit when IFCEdit core
// arrives with destructive ops that justify a richer history (groups,
// transactions, branching). Entries of DIFFERENT modules are
// independent: undoing one doesn't ripple into others. Within a module
// the history is a stack: only the most recent live entry can be undone
// and only the most recently undone one redone (see podeDesfazer /
// podeRefazer). Out of order, the viewer's visibility snapshots
// restored states that mixed actions together.

export interface HistoryEntry {
  id: string
  module: string // 'boq' | 'ifc-edit' | …
  label: string // human-readable, e.g. "BoQ: 154 walls (canónico)"
  ts: number
  // Set when the entry has been undone and is therefore available
  // for redo. Mutually exclusive with the "live" state.
  undone: boolean
  // Module-supplied callbacks. Either or both may be undefined when
  // the action is non-reversible (right now everything supplies both).
  undo?: () => void | Promise<void>
  redo?: () => void | Promise<void>
  // Models the entry acts on (by viewer model id). When one of them is
  // closed the entry goes away: the ids are file names, and a new
  // version of the same file would receive the old undo.
  modelIds?: string[]
}

export interface HistorySlice {
  history: HistoryEntry[]
  pushHistoryEntry: (
    entry: Omit<HistoryEntry, 'id' | 'ts' | 'undone'>,
  ) => string
  setHistoryEntryUndone: (id: string, undone: boolean) => void
  removeHistoryEntry: (id: string) => void
  removeHistoryEntriesForModel: (modelId: string) => void
  clearHistory: () => void
}

/** A entrada pode ser desfeita agora? Só a mais recente ainda activa do
 *  seu módulo. */
export function podeDesfazer(history: HistoryEntry[], id: string): boolean {
  const e = history.find((h) => h.id === id)
  if (!e || e.undone || !e.undo) return false
  const activas = history.filter((h) => h.module === e.module && !h.undone)
  return activas[activas.length - 1]?.id === id
}

/** A entrada pode ser refeita agora? Só a última que se desfez no seu
 *  módulo, que é a primeira das desfeitas pela ordem do histórico. */
export function podeRefazer(history: HistoryEntry[], id: string): boolean {
  const e = history.find((h) => h.id === id)
  if (!e || !e.undone || !e.redo) return false
  const desfeitas = history.filter((h) => h.module === e.module && h.undone)
  return desfeitas[0]?.id === id
}

let historyCounter = 0

export const createHistorySlice: StateCreator<HistorySlice> = (set) => ({
  history: [],

  pushHistoryEntry: (entry) => {
    const id = `h-${++historyCounter}-${Date.now().toString(36)}`
    // Uma acção nova no módulo descarta o que lá estava desfeito: refazê-lo
    // por cima do estado novo não repõe nada de coerente.
    set((state) => ({
      history: [
        ...state.history.filter(
          (h) => !(h.module === entry.module && h.undone),
        ),
        { ...entry, id, ts: Date.now(), undone: false },
      ],
    }))
    return id
  },

  setHistoryEntryUndone: (id, undone) =>
    set((state) => ({
      history: state.history.map((h) =>
        h.id === id ? { ...h, undone } : h,
      ),
    })),

  removeHistoryEntry: (id) =>
    set((state) => ({
      history: state.history.filter((h) => h.id !== id),
    })),

  removeHistoryEntriesForModel: (modelId) =>
    set((state) => ({
      history: state.history.filter(
        (h) => !h.modelIds || !h.modelIds.includes(modelId),
      ),
    })),

  clearHistory: () => set({ history: [] }),
})
